import { create, StateCreator } from "zustand";
import { persist } from "zustand/middleware";
import { VideoPlayerHandle, YTPlayerState } from "@/lib/yt";
import { consoleLog, objDiff } from "@/lib/logging";

export const YtStates = {
  "-1": "unstarted",
  "0": "ended",
  "1": "playing",
  "2": "paused",
  "3": "buffering",
  "5": "video cued",
};

export const parseAndBuildArtist = (it: Track) => ({
  artist: it.artist as string || '',
  youtube: it.youtube,
  promoLink: it.promoLink as string || '',
  description: it.description || '',
  volume: it.volume,
});

const hasLocalStorage = (() => {
  if (typeof window === 'undefined') return false;
  try {
    const s = window.localStorage;
    const k = '__storage_test__';
    s.setItem(k, '1');
    s.removeItem(k);
    return true;
  } catch {
    return false;
  }
})();

export type CmsYouTubeType = {
  vid: string;
  start: number;
  end?: number;
  title?: string;
};

export type CmsSiteSettingsMusic = {
  article: string;
  description?: string;
  promoLink: string;
  youtube: CmsYouTubeType;
  volume?: number;
}

export type Track = {
  artist: string;
  youtube: CmsYouTubeType;
  promoLink?: string;
  description?: string;
  volume?: number;
};

type MusicState = {
  playIntent: boolean;
  playlist: Track[];
  currentTrackId: string;
  positions: Record<string, number>;
  loading?: boolean;
  error?: string | null;
  playerRef: VideoPlayerHandle | null;
  playerState: YTPlayerState;
  trigger?: string;
  getCurrentTrack: () => Track | undefined;
  setPlayIntent: (v: boolean) => void;
  setPlaylist: (p: Track[]) => void;
  setCurrentTrackId: (id?: string | null) => void;
  setPlayerState: (v: YTPlayerState) => void;
  setPosition: (id: string, seconds: number) => void;
  setPlayerRef: (ref: VideoPlayerHandle | null) => void;
};

const createState: StateCreator<MusicState> = (set, get) => ({
  playIntent: false,
  playlist: [] as Track[],
  currentTrackId: "",
  playerState: -1,
  positions: {} as Record<string, number>,
  loading: false,
  playerRef: null,
  error: null as string | null,
  getCurrentTrack: () => get().playlist.find(t => t?.youtube?.vid === get().currentTrackId),
  setPlayIntent: (v: boolean) => set(() => ({ playIntent: v, trigger: "setPlayIntent" })),
  setPlaylist: (p: Track[]) => set(() => ({ playlist: p, trigger: "setPlaylist" })),
  setPlayerState: (v: YTPlayerState) => set(() => ({ playerState: v, trigger: "setPlayerState" })),
  setCurrentTrackId: (id?: string | null) => set(() => ({ currentTrackId: id ?? "", trigger: "setCurrentTrackId" })),
  setPlayerRef: (ref: VideoPlayerHandle | null) => set(() => ({ playerRef: ref, trigger: "setPlayerRef" })),
  setPosition: (id: string, seconds: number) => set((s: MusicState) => ({ positions: { ...s.positions, [id]: seconds }, trigger: "setPosition" })),
});

const maybePersisted: StateCreator<MusicState> = hasLocalStorage
  ? (persist(createState, { name: 'caroadhouse-music' as const }) as unknown as StateCreator<MusicState>)
  : createState;

export const useMusicStore = create<MusicState>()(maybePersisted);

useMusicStore.subscribe((state: MusicState, preState: MusicState) => {
  const { trigger } = state;
  const preStateNoTrigger = { ...preState } as Record<string, unknown>;
  const stateNoTrigger = { ...state } as Record<string, unknown>;

  delete preStateNoTrigger['trigger'];
  delete stateNoTrigger['trigger'];

  const diff = objDiff(preStateNoTrigger, stateNoTrigger);
  if (diff) {
    consoleLog(trigger ?? "update", diff);
  }
});

export default useMusicStore;
